import { useEffect, useMemo, useState } from "react";
import { getClinicProfile, type ClinicProfileResponse } from "../lib/vetcanApi";
import {
  DEFAULT_TENANT_KEY,
  getClinicProfileFallback,
  normalizeTenantHostname,
} from "../lib/tenantConfig";
import { resolveTenantKeyRuntime } from "../lib/runtimeTenant";

export type ClinicProfileSource = "api" | "fallback";
export type ClinicProfileFetchStatus = "ok" | "failed" | "skipped";

type UseClinicProfileOptions = {
  tenantKey?: string;
  skipApi?: boolean;
};

type UseClinicProfileState = {
  tenantKey: string;
  profile: ClinicProfileResponse;
  source: ClinicProfileSource;
  isLoading: boolean;
  error: string | null;
  devNotice: string | null;
  lastFetchStatus: ClinicProfileFetchStatus;
  lastFetchAt: string | null;
};

function buildFallbackProfile(tenantKey: string): ClinicProfileResponse {
  const fallback = getClinicProfileFallback(tenantKey);
  return {
    tenantKey: fallback.tenantKey,
    displayName: fallback.displayName,
    addressText: fallback.addressText,
    hoursText: fallback.hoursText,
    parkingText: fallback.parkingText,
    renewalsPhone: fallback.renewalsPhone,
    supportPhone: fallback.supportPhone,
    noMedicalAdviceNotice: fallback.noMedicalAdviceNotice,
  };
}

export function useClinicProfile(options: UseClinicProfileOptions = {}) {
  const resolvedTenantKey = useMemo(() => {
    const providedKey = normalizeTenantHostname(options.tenantKey || "");
    return providedKey || resolveTenantKeyRuntime();
  }, [options.tenantKey]);

  const fallbackProfile = useMemo(
    () => buildFallbackProfile(resolvedTenantKey),
    [resolvedTenantKey]
  );

  const [state, setState] = useState<UseClinicProfileState>({
    tenantKey: resolvedTenantKey,
    profile: fallbackProfile,
    source: "fallback",
    isLoading: !options.skipApi,
    error: null,
    devNotice: null,
    lastFetchStatus: "skipped",
    lastFetchAt: null,
  });

  useEffect(() => {
    let active = true;

    setState((previous) => ({
      ...previous,
      tenantKey: resolvedTenantKey,
      profile: fallbackProfile,
      source: "fallback",
      isLoading: !options.skipApi,
      error: null,
      devNotice: null,
      lastFetchStatus: "skipped",
      lastFetchAt: null,
    }));

    if (options.skipApi) {
      if (!active) return;
      setState((previous) => ({
        ...previous,
        isLoading: false,
        lastFetchStatus: "skipped",
      }));
      return () => {
        active = false;
      };
    }

    const loadProfile = async () => {
      const attemptedAt = new Date().toISOString();
      try {
        const profile = await getClinicProfile(resolvedTenantKey);
        const normalizedRequestedTenant = normalizeTenantHostname(resolvedTenantKey);
        const normalizedProfileTenant = normalizeTenantHostname(profile.tenantKey);
        const tenantMismatch =
          normalizedRequestedTenant !== DEFAULT_TENANT_KEY &&
          normalizedProfileTenant !== normalizedRequestedTenant;

        if (tenantMismatch) {
          throw new Error(
            `Clinic profile tenant mismatch (requested ${normalizedRequestedTenant}, got ${normalizedProfileTenant || "unknown"})`
          );
        }

        if (!active) return;
        setState({
          tenantKey: resolvedTenantKey,
          profile,
          source: "api",
          isLoading: false,
          error: null,
          devNotice: null,
          lastFetchStatus: "ok",
          lastFetchAt: attemptedAt,
        });
      } catch (error) {
        if (!active) return;
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load clinic profile";
        setState({
          tenantKey: resolvedTenantKey,
          profile: fallbackProfile,
          source: "fallback",
          isLoading: false,
          error: errorMessage,
          devNotice: import.meta.env.DEV
            ? `Using fallback clinic profile for ${resolvedTenantKey}.`
            : null,
          lastFetchStatus: "failed",
          lastFetchAt: attemptedAt,
        });
      }
    };

    void loadProfile();

    return () => {
      active = false;
    };
  }, [fallbackProfile, options.skipApi, resolvedTenantKey]);

  return state;
}
